import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Leaf, Globe2, Layers, Package, Sparkles, Beaker, Truck, Award, Boxes, Wheat, Flame } from "lucide-react";
import Reveal from "../components/Reveal";
import RequestQuoteModal from "../components/RequestQuoteModal";
import { PRODUCTS, BLOG_POSTS } from "../data/products";
import { MAKHANA_PRODUCTS, PULSES_PRODUCTS } from "../data/catalogue";

const WHY = [
  { icon: Award, title: "Premium Quality", body: "Export-grade spices with laboratory-verified moisture, colour and purity specifications." },
  { icon: Leaf, title: "Authentic Sourcing", body: "Directly from India's most celebrated spice-growing regions — from Kerala to Rajasthan." },
  { icon: Globe2, title: "Reliable Export Partner", body: "Full export documentation, phytosanitary certificates and consistent delivery timelines." },
  { icon: ShieldCheck, title: "Strict Quality Control", body: "HACCP-aligned processing, sortex sorting, and steam-sterilization ready facilities." },
  { icon: Boxes, title: "Bulk & Retail Supply", body: "From 25kg export bags to retail-ready SUPs — one supplier for every packaging need." },
  { icon: Layers, title: "Private Label", body: "End-to-end OEM services with custom blends, packaging and destination-market labelling." },
  { icon: Sparkles, title: "Competitive Pricing", body: "Vertically integrated sourcing keeps our per-kilo economics ahead of the market." },
  { icon: Truck, title: "Timely Shipment", body: "Trusted freight forwarders and clean paperwork mean your containers move on schedule." },
  { icon: Beaker, title: "Responsive Support", body: "A dedicated export coordinator for every buyer — from first sample to third repeat order." },
];

const TIMELINE = [
  "Premium Raw Material Selection",
  "Cleaning",
  "Sorting",
  "Grinding",
  "Laboratory Testing",
  "Quality Inspection",
  "Packaging",
  "Export Documentation",
  "Container Loading",
  "Worldwide Dispatch",
];

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  // Mixed featured: 4 spices + 1 makhana + 1 pulse for a richer cross-catalogue teaser
  const featured = [
    { ...PRODUCTS[0], _base: "/products" },
    { ...PRODUCTS[6], _base: "/products" }, // black pepper
    { ...PRODUCTS[7], _base: "/products" }, // green cardamom
    { ...MAKHANA_PRODUCTS[0], _base: "/makhana" },
    { ...PRODUCTS[1], _base: "/products" }, // red chilli
    { ...PULSES_PRODUCTS[5], _base: "/pulses" }, // kabuli chickpeas
  ];

  const CATEGORIES = [
    { to: "/products", title: "Whole Spices", desc: "Peppercorns, cardamom, cloves, cinnamon, bay leaves, seeds.", image: PRODUCTS[6].image, tint: "#2A1E14", icon: Sparkles, count: PRODUCTS.filter(p => /seeds|pepper|cardamom|cloves|cinnamon|leaves|nutmeg|mace/i.test(p.name)).length + "+ SKUs" },
    { to: "/products", title: "Ground Spices", desc: "Turmeric, chilli, coriander, cumin powders and signature blends.", image: PRODUCTS[0].image, tint: "#D89620", icon: Flame, count: PRODUCTS.filter(p => /powder|masala/i.test(p.name)).length + " SKUs" },
    { to: "/makhana", title: "Makhana (Fox Nuts)", desc: "Raw, roasted and bulk fox nuts from Bihar's finest ponds.", image: MAKHANA_PRODUCTS[0].image, tint: "#D4B98A", icon: Leaf, count: MAKHANA_PRODUCTS.length + " Formats" },
    { to: "/pulses", title: "Pulses & Lentils", desc: "Dals, whole beans, chickpeas and kidney beans — bulk and retail.", image: PULSES_PRODUCTS[0].image, tint: "#D9A548", icon: Wheat, count: PULSES_PRODUCTS.length + " SKUs" },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[100vh] bg-brand-ink text-brand-ivory overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="/images/hero/hero-main.png"
            alt=""
            className="w-full h-full object-cover opacity-60"
            onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/30296301/pexels-photo-30296301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-transparent to-brand-ink/60" />
        </div>

        <div className="container-luxe relative pt-40 pb-24 min-h-[100vh] flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8 animate-fade-up">
              <span className="w-10 h-px bg-brand-gold" />
              <span className="overline">Manufacturer · Supplier · Exporter</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-ivory animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Premium <em className="italic text-brand-gold font-light">Indian Spices</em><br />
              exported to the world.
            </h1>
            <p className="mt-8 text-brand-ivory/70 text-lg leading-relaxed max-w-xl animate-fade-up" style={{ animationDelay: "0.25s" }}>
              Supplying premium-quality Indian spices with consistency, food safety, export-ready packaging and reliable global supply for international buyers.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              <button onClick={() => setQuoteOpen(true)} className="luxe-btn-primary" data-testid="hero-request-quote">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/products" className="luxe-btn-outline-light" data-testid="hero-view-products">
                View Products
              </Link>
            </div>
          </div>

          <div className="absolute bottom-10 left-6 md:left-10 lg:left-16 right-6 md:right-10 lg:right-16 flex flex-wrap items-end justify-between gap-6 text-brand-ivory/50 text-[11px] tracking-[0.28em] uppercase">
            <div>Bringing India's Finest Spices to the World</div>
            <div className="hidden md:block">Scroll to explore ↓</div>
          </div>
        </div>
      </section>

      {/* INTRO — Editorial */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
            <span className="overline">The House of DeepRoots</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] text-brand-charcoal">
              A modern Indian export house, built on <em className="italic text-brand-green">ancient soil</em>.
            </h2>
            <span className="divider-gold" />
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6 lg:col-start-7 space-y-6 text-brand-charcoal/75 text-[17px] leading-[1.8]">
            <p>India has been the world's spice capital for over two millennia. Our work is to bring that heritage forward — with the modern processing, food-safety discipline and export reliability that international buyers require today.</p>
            <p>Every spice we ship carries the same commitment: authentic origin, laboratory-verified quality, and packaging built for the demands of global distribution. From bulk 50kg export bags to retail stand-up pouches, we build supply relationships designed to last across container after container.</p>
            <div className="pt-4 flex items-center gap-4">
              <Link to="/about" className="text-brand-green font-semibold text-[13px] tracking-[0.22em] uppercase link-underline inline-flex items-center gap-2" data-testid="home-about-link">
                More about us <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <Reveal className="max-w-2xl mb-16">
            <span className="overline">Our Product Categories</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] text-brand-charcoal">
              Four <em className="italic text-brand-green">export-ready</em> categories, one Indian house.
            </h2>
            <span className="divider-gold" />
            <p className="text-brand-charcoal/60 text-[16px] leading-relaxed">Spices, superfoods and pulses — sourced from the regions that grow them best, processed to laboratory-verified export standards, and packed for global retail, foodservice and private label buyers.</p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <Link to={c.to} className="group relative block aspect-[3/4] overflow-hidden bg-brand-ink" data-testid={`home-category-${c.title.toLowerCase().split(" ")[0]}`}>
                  <div className="absolute inset-0" style={{ backgroundColor: c.tint, opacity: 0.4 }} />
                  <img src={c.image} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-[1.06] transition-transform duration-[900ms] ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/40 to-brand-ink/10" />
                  <div className="absolute top-6 left-6 flex items-center gap-3">
                    <div className="w-9 h-9 border border-brand-gold/70 flex items-center justify-center text-brand-gold bg-brand-ink/50 backdrop-blur-sm">
                      <c.icon className="w-4 h-4" />
                    </div>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-brand-ivory/70">{c.count}</span>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 text-brand-ivory">
                    <h3 className="font-serif text-2xl leading-tight">{c.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-brand-ivory/70 max-w-xs">{c.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-brand-gold border-b border-brand-gold/40 pb-1 group-hover:border-brand-gold transition-colors">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="py-24 md:py-32 bg-brand-ivory-warm relative overflow-hidden">
        <div className="container-luxe">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="overline">Featured Selection</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">A curated cross-section of our catalogue.</h2>
              <span className="divider-gold" />
            </div>
            <Link to="/products" className="luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory" data-testid="home-view-all-products">
              View Spice Catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <Link to={`${p._base}/${p.slug}`} className="group block" data-testid={`product-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-brand-ink">
                    <div className="absolute inset-0" style={{ backgroundColor: p.color, opacity: 0.4 }} />
                    <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-brand-ivory/80 border border-brand-ivory/30 px-2.5 py-1">{p.grade.split("/")[0].trim()}</div>
                    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-brand-ivory">
                      <div>
                        <div className="font-serif text-2xl leading-tight">{p.name}</div>
                        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ivory/60 italic mt-1">{p.tagline}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[12px] tracking-wide">
                    <span className="text-brand-charcoal/60">MOQ · {p.moq}</span>
                    <span className="text-brand-green font-semibold uppercase tracking-[0.2em] text-[11px]">Enquire</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-brand-ink text-brand-ivory relative overflow-hidden grain">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.06]">
          <img src="/images/hero/hero-quality.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxe relative">
          <Reveal className="max-w-2xl mb-16">
            <span className="overline">Why Buyers Choose Us</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Quality is not a claim. It is a discipline.</h2>
            <span className="divider-gold" />
            <p className="text-brand-ivory/60 text-[17px] leading-relaxed">Nine reasons international importers, private label brands and food manufacturers make DeepRoots Spice their long-term Indian partner.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-y-14 gap-x-10">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 60} className="border-l border-brand-gold/30 pl-6">
                <div className="w-10 h-10 border border-brand-gold/60 flex items-center justify-center mb-5 text-brand-gold">
                  <w.icon className="w-4 h-4" />
                </div>
                <h4 className="font-serif text-2xl mb-2">{w.title}</h4>
                <p className="text-brand-ivory/60 text-[14.5px] leading-relaxed">{w.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY TIMELINE */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <span className="overline">From Field to Container</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] text-brand-charcoal">
              A <em className="italic text-brand-green">ten-step</em> quality journey.
            </h2>
            <span className="divider-gold" />
            <p className="text-brand-charcoal/60 leading-relaxed">Every export lot passes through the same rigorous protocol — because consistency is what builds international trust, container after container.</p>
            <Link to="/quality" className="mt-8 luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory inline-flex" data-testid="home-quality-link">
              Explore our process <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6">
            <ol className="relative border-l border-brand-gold/40 pl-8 space-y-8">
              {TIMELINE.map((step, i) => (
                <Reveal as="li" key={step} delay={i * 40} className="relative">
                  <span className="absolute -left-[41px] top-1 w-6 h-6 rounded-full bg-brand-ivory border border-brand-gold flex items-center justify-center text-[10px] font-semibold text-brand-green">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="font-serif text-xl text-brand-charcoal">{step}</div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* GLOBAL MARKETS BAND */}
      <section className="py-24 md:py-32 bg-brand-green text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="/images/hero/hero-export.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxe relative grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <span className="overline text-brand-gold-soft">Global Reach</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Serving global markets with premium Indian spice solutions.</h2>
            <span className="block w-16 h-px bg-brand-gold my-6" />
            <p className="text-brand-ivory/75 max-w-xl leading-relaxed">North America, Europe, the Middle East, Asia-Pacific and Africa — our export operations are structured to handle the documentation, logistics and quality expectations of buyers across five continents.</p>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-4 lg:col-start-9 flex lg:justify-end">
            <Link to="/export-markets" className="luxe-btn-outline-light" data-testid="home-markets-link">
              Explore markets <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRIVATE LABEL + PACKAGING */}
      <section className="py-24 md:py-32 bg-brand-ivory-warm">
        <div className="container-luxe grid md:grid-cols-2 gap-10">
          <Reveal>
            <Link to="/private-label" className="group block relative aspect-[4/3] overflow-hidden bg-brand-ink" data-testid="home-privatelabel-card">
              <img src="/images/hero/hero-packaging.png" alt="Private label" className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-[900ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/30 to-transparent" />
              <div className="absolute inset-0 p-10 flex flex-col justify-end text-brand-ivory">
                <span className="overline text-brand-gold-soft">OEM & Private Label</span>
                <h3 className="font-serif text-4xl mt-3 max-w-xs">Build your own spice brand.</h3>
                <div className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-[0.22em] uppercase text-brand-gold">Explore Private Label <ArrowUpRight className="w-4 h-4" /></div>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <Link to="/packaging" className="group block relative aspect-[4/3] overflow-hidden bg-brand-earth" data-testid="home-packaging-card">
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="w-32 h-32 text-brand-gold/20" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-earth via-brand-earth/80 to-brand-earth/40" />
              <div className="absolute inset-0 p-10 flex flex-col justify-end text-brand-ivory">
                <span className="overline text-brand-gold-soft">Packaging Formats</span>
                <h3 className="font-serif text-4xl mt-3 max-w-xs">From 25kg bags to nitrogen-flushed pouches.</h3>
                <div className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-[0.22em] uppercase text-brand-gold">See packaging options <ArrowUpRight className="w-4 h-4" /></div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="overline">The Trade Journal</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">Insights for global spice buyers.</h2>
              <span className="divider-gold" />
            </div>
            <Link to="/blog" className="luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory" data-testid="home-blog-link">
              Read all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {BLOG_POSTS.slice(0, 3).map((post, i) => (
              <Reveal key={post.slug} delay={i * 100}>
                <Link to={`/blog/${post.slug}`} className="group block" data-testid={`home-blog-${post.slug}`}>
                  <div className="text-[11px] tracking-[0.24em] uppercase text-brand-gold mb-3">{post.category} · {post.read_time}</div>
                  <h4 className="font-serif text-2xl leading-snug text-brand-charcoal group-hover:text-brand-green transition-colors">{post.title}</h4>
                  <p className="mt-4 text-brand-charcoal/60 text-[15px] leading-relaxed line-clamp-3">{post.excerpt}</p>
                  <div className="mt-6 text-[12px] tracking-[0.22em] uppercase text-brand-charcoal/50">{post.date}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-brand-ink text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="/images/hero/hero-main.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-brand-ink/70" />
        <div className="container-luxe relative text-center max-w-3xl mx-auto">
          <Reveal>
            <span className="overline">Ready when you are</span>
            <h2 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.05]">
              Let's <em className="italic text-brand-gold">move</em> your first container.
            </h2>
            <p className="mt-8 text-brand-ivory/70 max-w-xl mx-auto leading-relaxed">Share your requirements — spice, grade, packaging, destination port — and our export team will respond with pricing, samples and lead-times within 24 business hours.</p>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <button onClick={() => setQuoteOpen(true)} className="luxe-btn-primary" data-testid="home-cta-quote">Request a Quote <ArrowRight className="w-4 h-4" /></button>
              <Link to="/contact" className="luxe-btn-outline-light" data-testid="home-cta-contact">Contact us</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <RequestQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
